import * as React from "react";
import { Form } from "semantic-ui-react";
import * as _ from "lodash";
import MenuButton from "../MenuButton/MenuButton";

export type ButtonProps = {
  callback: () => void;
  enabledTooltip?: string;
  disabledTooltip?: string;
  disabled?: boolean;
};

export type EditorProps<T> = {
  itemName: string;
  originalObject: T;
  editedObject: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: any;
  hasWritePermission: boolean;
  isValid: boolean;
  cancel: () => void;
  update: () => void;
  create: () => void;
  copy?: () => void;
  remove?: ButtonProps;
  copyAccess?: boolean;
};

function Editor<T>({
  itemName,
  originalObject,
  editedObject,
  children,
  hasWritePermission,
  isValid,
  cancel,
  update,
  create,
  copy = undefined,
  remove = undefined,
  copyAccess = true
}: EditorProps<T>): JSX.Element {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (
    (editedObject as any)?.liftParameterBindings ||
    (editedObject as any)?.driveParameterBindings ||
    (editedObject as any)?.escalatorParameterBindings ||
    (editedObject as any)?.doorParameterBindings
  ) {
    const targetType = (editedObject as any)?.liftParameterBindings
      ? "liftParameterBindings"
      : (editedObject as any)?.driveParameterBindings
      ? "driveParameterBindings"
      : (editedObject as any)?.doorParameterBindings
      ? "doorParameterBindings"
      : "escalatorParameterBindings";
    const bindings = (editedObject as any)?.[targetType].map(
      /* eslint-disable @typescript-eslint/no-unused-vars */
      ({
        isDirty,
        ...rest
      }: {
        isDirty: boolean;
        kceVersion: any;
        driveVersion: any;
        escalatorVersion: any;
        doorVersion: any;
        kceProduct: any;
        driveProduct: any;
      }) => {
        targetType == "liftParameterBindings"
          ? ((rest.kceVersion.url = null), (rest.kceProduct.url = null))
          : targetType == "driveParameterBindings"
          ? (rest.driveVersion.url = null)
          : targetType == "doorParameterBindings"
          ? (rest.doorVersion.url = null)
          : (rest.escalatorVersion.url = null);
        return rest;
      }
    );
    editedObject = { ...editedObject, [targetType]: bindings };
  } else if ((editedObject as any)?.kceVersion && (editedObject as any)?.kceProduct) {
    (editedObject as any).kceVersion.url = null;
    (editedObject as any).kceProduct.url = null;
  }

  const removeNewLine = (jsonobject: any) =>
    JSON.stringify(jsonobject, (key, value) => {
      if (typeof value === "string") {
        return value.replace(/[\r\n]/g, ""); // Replace or remove \r\n
      }
      return value; // Return non-string values as-is
    });

  const hasParameterEdited = !_.isEqual(removeNewLine(editedObject), removeNewLine(originalObject));
  const isNew = !originalObject;
  let CancelButton;
  
  if (isNew) {
    CancelButton =
      isNew && !isValid ? (
        <MenuButton type="Close" handleOnClick={cancel} />
      ) : (
        <MenuButton type="Cancel" handleOnClick={cancel} />
      );
  } else {
    CancelButton = hasParameterEdited ? (
      <MenuButton type="Cancel" handleOnClick={cancel} />
    ) : (
      <MenuButton type="Close" handleOnClick={cancel} />
    );
  }

  return (
    <div className="edit-container">
      <div className="edit-header">
        <span>
          {hasWritePermission ? (isNew ? "Add" : "Edit") : "View"} {itemName}
        </span>

        <div className="edit-header-actions">
          {hasParameterEdited && !isNew && (
            <div className="edit-notification">You have unsaved changes!</div>
          )}
          {!isNew ? (
            <MenuButton
              type="Save"
              handleOnClick={update}
              disabled={!hasWritePermission || !hasParameterEdited || !isValid}
            />
          ) : (
            <MenuButton
              type="Save"
              handleOnClick={create}
              disabled={!hasWritePermission || !hasParameterEdited || !isValid}
            />
          )}
          {CancelButton}
        </div>
      </div>
      {!isNew && (copy || remove) && itemName != "Enum Option Name" && (
        <div className="edit-action-menu">
          {!!remove && hasWritePermission && (
            <MenuButton
              showtext={true}
              type="Delete"
              source={remove.disabled ? remove.disabledTooltip : remove.enabledTooltip}
              handleOnClick={remove.callback}
              disabled={remove.disabled}
            />
          )}
          {!!copy && hasWritePermission && copyAccess && (
            <MenuButton showtext={true} type="Copy" source={itemName} handleOnClick={copy} />
          )}
        </div>
      )}
      <div className="edit-form-form">
        <Form>{children}</Form>
      </div>
    </div>
  );
}

export default Editor;
